import React from "react";

const TeamDetailFixturesTab = ({
  fixtures,
  decodedTeamName,
  onGameClick,
  loading,
}) => {
  if (loading) {
    return (
      <div className="space-y-10">
        <div>
          <div className="h-6 w-24 bg-white/10 rounded animate-pulse mb-4" />
          <div className="bg-white/[0.03] rounded-xl border border-white/[0.06] overflow-hidden">
            {[...Array(5)].map((_, i) => (
              <div
                className="flex items-center gap-4 p-4 border-b border-white/[0.04] animate-pulse"
                key={i}
              >
                <div className="w-20 h-4 bg-white/10 rounded" />
                <div className="w-6 h-6 rounded-full bg-white/10" />
                <div className="flex-1 h-4 bg-white/10 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return "TBD";
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "TBD";
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const getTeamNames = (game) => ({
    home: game.teams?.home?.name || game.strHomeTeam || game.homeTeam || "Home",
    away: game.teams?.away?.name || game.strAwayTeam || game.awayTeam || "Away",
    homeLogo:
      game.teams?.home?.logo || game.homeTeamLogo || game.strHomeTeamBadge,
    awayLogo:
      game.teams?.away?.logo || game.awayTeamLogo || game.strAwayTeamBadge,
  });

  const isHome = (game) => {
    const { home } = getTeamNames(game);

    return (
      home.toLowerCase().includes(decodedTeamName.toLowerCase()) ||
      decodedTeamName.toLowerCase().includes(home.toLowerCase())
    );
  };

  if (fixtures.upcoming.length === 0 && fixtures.recent.length === 0) {
    return (
      <div className="text-center py-20">
        <i className="fas fa-calendar-alt text-5xl text-white/10 mb-4" />
        <p className="text-white/40 text-lg">No fixture data available</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Upcoming */}
      {fixtures.upcoming.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-4">Upcoming</h3>
          <div className="bg-white/[0.03] rounded-xl border border-white/[0.06] overflow-hidden divide-y divide-white/[0.04]">
            {fixtures.upcoming.map((game, i) => {
              const names = getTeamNames(game);
              const date = game.fixture?.date || game.date || game.dateEvent;
              const venue =
                game.fixture?.venue?.name || game.venue?.name || game.strVenue;
              const home = isHome(game);
              const opponent = home ? names.away : names.home;
              const opponentLogo = home ? names.awayLogo : names.homeLogo;

              return (
                <button
                  className="w-full flex items-center gap-4 p-4 hover:bg-white/[0.03] transition-colors text-left focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:ring-inset"
                  key={game.fixture?.id || game.id || i}
                  tabIndex={0}
                  onClick={() => onGameClick(game)}
                >
                  <div className="w-20 text-xs text-white/40 flex-shrink-0">
                    <div>{formatDate(date)}</div>
                    <div className="text-white/25">{formatTime(date)}</div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-white/30 w-8 flex-shrink-0">
                    {home ? (
                      <span className="px-1.5 py-0.5 rounded bg-white/5">
                        H
                      </span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded bg-white/5">
                        A
                      </span>
                    )}
                  </div>
                  {opponentLogo && (
                    <img
                      alt=""
                      className="w-6 h-6 object-contain flex-shrink-0"
                      src={opponentLogo}
                    />
                  )}
                  <span className="text-white/80 text-sm font-medium flex-1 truncate">
                    {opponent}
                  </span>
                  {venue && (
                    <span className="text-white/20 text-xs hidden md:block">
                      {venue}
                    </span>
                  )}
                  <i className="fas fa-chevron-right text-white/15 text-xs" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Results */}
      {fixtures.recent.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-4">
            Recent Results
          </h3>
          <div className="bg-white/[0.03] rounded-xl border border-white/[0.06] overflow-hidden divide-y divide-white/[0.04]">
            {fixtures.recent.map((game, i) => {
              const names = getTeamNames(game);
              const date = game.fixture?.date || game.date || game.dateEvent;
              const homeScore =
                game.goals?.home ??
                game.score?.fulltime?.home ??
                game.homeTeamScore ??
                game.intHomeScore ??
                null;
              const awayScore =
                game.goals?.away ??
                game.score?.fulltime?.away ??
                game.awayTeamScore ??
                game.intAwayScore ??
                null;
              const home = isHome(game);
              const teamScore = home ? homeScore : awayScore;
              const oppScore = home ? awayScore : homeScore;
              const opponent = home ? names.away : names.home;
              const opponentLogo = home ? names.awayLogo : names.homeLogo;

              let result = "D";
              let resultColor =
                "text-yellow-400 bg-yellow-500/15 border-yellow-500/30";

              if (teamScore !== null && oppScore !== null) {
                if (Number(teamScore) > Number(oppScore)) {
                  result = "W";
                  resultColor =
                    "text-green-400 bg-green-500/15 border-green-500/30";
                } else if (Number(teamScore) < Number(oppScore)) {
                  result = "L";
                  resultColor = "text-red-400 bg-red-500/15 border-red-500/30";
                }
              }

              return (
                <button
                  className="w-full flex items-center gap-4 p-4 hover:bg-white/[0.03] transition-colors text-left focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:ring-inset"
                  key={game.fixture?.id || game.id || i}
                  tabIndex={0}
                  onClick={() => onGameClick(game)}
                >
                  <div className="w-20 text-xs text-white/40 flex-shrink-0">
                    {formatDate(date)}
                  </div>
                  {opponentLogo && (
                    <img
                      alt=""
                      className="w-6 h-6 object-contain flex-shrink-0"
                      src={opponentLogo}
                    />
                  )}
                  <span className="text-white/80 text-sm font-medium flex-1 truncate">
                    {home ? "vs" : "at"} {opponent}
                  </span>
                  {teamScore !== null && (
                    <span className="text-white text-sm font-bold">
                      {teamScore} - {oppScore}
                    </span>
                  )}
                  <span
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border ${resultColor}`}
                  >
                    {result}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamDetailFixturesTab;
